/**
 * Onboarding calculation utilities — BMI, Body Fat, BMR, TDEE, Macros
 */

export function getBMICategory(bmi) {
  if (bmi < 18.5) return { label: "Underweight", color: "#F59E0B" };
  if (bmi < 25) return { label: "Normal", color: "#10B981" };
  if (bmi < 30) return { label: "Overweight", color: "#F59E0B" };
  return { label: "Obese", color: "#EF4444" };
}

export function getBodyFatCategory(bf, isMale) {
  if (isMale) {
    if (bf < 6) return { label: "Essential", color: "#EF4444" };
    if (bf < 14) return { label: "Athletic", color: "#0EA5E9" };
    if (bf < 18) return { label: "Fitness", color: "#10B981" };
    if (bf < 25) return { label: "Average", color: "#F59E0B" };
    return { label: "Above Average", color: "#EF4444" };
  }
  if (bf < 14) return { label: "Essential", color: "#EF4444" };
  if (bf < 21) return { label: "Athletic", color: "#0EA5E9" };
  if (bf < 25) return { label: "Fitness", color: "#10B981" };
  if (bf < 32) return { label: "Average", color: "#F59E0B" };
  return { label: "Above Average", color: "#EF4444" };
}

export function calculateMacros(tdee, goal, weight) {
  let calories = tdee;
  let proteinRatio, carbRatio, fatRatio;

  switch (goal) {
    case "weight_loss":
      calories = tdee - 500;
      proteinRatio = 0.35; carbRatio = 0.35; fatRatio = 0.30;
      break;
    case "muscle_gain":
      calories = tdee + 300;
      proteinRatio = 0.30; carbRatio = 0.45; fatRatio = 0.25;
      break;
    case "maintenance":
      proteinRatio = 0.25; carbRatio = 0.45; fatRatio = 0.30;
      break;
    default:
      proteinRatio = 0.25; carbRatio = 0.45; fatRatio = 0.30;
  }

  // Protein preserved (satiety + lean-mass), then enforce a healthy fat floor,
  // carbs absorb the remainder so total calories stay constant.
  const proteinG = (calories * proteinRatio) / 4;
  const rawFatG = (calories * fatRatio) / 9;

  // Minimum fat floor: max(0.8 g/kg, 25% of kcal) — prevents unhealthy low-fat
  // targets, especially for peri/menopausal women (essential-fat & hormone support).
  const fatFloorG = Math.max(0.8 * weight, (calories * 0.25) / 9);
  const fatG = Math.max(rawFatG, fatFloorG);
  const fatFloorApplied = fatG > rawFatG + 0.01;

  // Recompute carbs from remaining calories after protein + (floored) fat.
  const remainingCarbCal = calories - proteinG * 4 - fatG * 9;
  const carbsG = Math.max(0, remainingCarbCal / 4);

  return {
    calories: Math.round(calories),
    protein: Math.round(proteinG),
    carbs: Math.round(carbsG),
    fat: Math.round(fatG),
    proteinPerKg: Math.round(proteinG / weight * 10) / 10,
    fatPerKg: Math.round(fatG / weight * 10) / 10,
    fatFloorApplied
  };
}

export function calculateMetrics(formData) {
  const height = parseFloat(formData.height) / 100;
  const weight = parseFloat(formData.weight);
  const age = parseInt(formData.age);
  const neck = parseFloat(formData.neckCircumference);
  const waist = parseFloat(formData.waistCircumference);
  const hip = parseFloat(formData.hipCircumference);
  const isMale = formData.gender === "male";

  const bmi = weight / (height * height);

  let bodyFat;
  if (isMale) {
    bodyFat = 495 / (1.0324 - 0.19077 * Math.log10(waist - neck) + 0.15456 * Math.log10(height * 100)) - 450;
  } else {
    bodyFat = 495 / (1.29579 - 0.35004 * Math.log10(waist + hip - neck) + 0.22100 * Math.log10(height * 100)) - 450;
  }

  let bmr;
  if (isMale) {
    bmr = 10 * weight + 6.25 * (height * 100) - 5 * age + 5;
  } else {
    bmr = 10 * weight + 6.25 * (height * 100) - 5 * age - 161;
  }

  const activityMultipliers = {
    sedentary: 1.2, light: 1.375, moderate: 1.55, active: 1.725, very_active: 1.9
  };
  const tdee = bmr * (activityMultipliers[formData.activityLevel] || 1.55);

  const leanMass = weight * (1 - bodyFat / 100);
  const waistHipRatio = hip > 0 ? waist / hip : null;
  const waistHeightRatio = waist / (height * 100);

  let idealWeight;
  if (isMale) {
    idealWeight = 50 + 2.3 * ((height * 100 / 2.54) - 60);
  } else {
    idealWeight = 45.5 + 2.3 * ((height * 100 / 2.54) - 60);
  }

  const macros = calculateMacros(tdee, formData.primaryGoal, weight);

  return {
    bmi: Math.round(bmi * 10) / 10,
    bmiCategory: getBMICategory(bmi),
    bodyFat: Math.round(bodyFat * 10) / 10,
    bodyFatCategory: getBodyFatCategory(bodyFat, isMale),
    bmr: Math.round(bmr),
    tdee: Math.round(tdee),
    leanMass: Math.round(leanMass * 10) / 10,
    waistHipRatio: waistHipRatio != null ? Math.round(waistHipRatio * 100) / 100 : null,
    waistHeightRatio: Math.round(waistHeightRatio * 100) / 100,
    idealWeight: Math.round(idealWeight * 10) / 10,
    weightDifference: Math.round((weight - idealWeight) * 10) / 10,
    macros
  };
}
